function hasValue(value) {
  return value !== undefined && value !== null && value !== '';
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeBooleanFlag(value, fallback = true) {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = String(value).trim().toLowerCase();
  if (['0', 'n', 'no', 'false'].includes(normalized)) return false;
  if (['1', 'y', 'yes', 'true'].includes(normalized)) return true;
  return fallback;
}

export function normalizeUpNext(value) {
  if (!value) return null;
  if (Array.isArray(value)) return value.length > 0 ? normalizeUpNext(value[0]) : null;
  if (typeof value !== 'object' || Object.keys(value).length === 0) return null;
  return {
    ...value,
    element_id: value.element_id ?? value.elementID ?? null,
    element_name: value.element_name ?? value.title ?? value.name ?? 'Next lesson',
    element_type: value.element_type ?? value.domain ?? '',
    lock_status: !!value.lock_status,
    lock_message: value.lock_message ?? 'This activity is locked right now.',
  };
}

function normalizeId(value) {
  if (value === undefined || value === null) return '';
  return String(value);
}

function isCertificationLikeUpNext(next) {
  const haystack = [
    next?.unit_name,
    next?.element_name,
    next?.attempt_name,
    next?.title,
    next?.name,
  ].filter(Boolean).join(' ').toLowerCase();

  return haystack.includes('certification') || haystack.includes('certificate');
}

export function shouldUseCompletionUpNext({ elementType, upNext, proctoredElementIds = [] }) {
  const next = normalizeUpNext(upNext);
  if (!next?.element_id) return null;

  // Old STEP only passed completion up-next through for W/F/D activities.
  if (!['W', 'F', 'D'].includes(elementType)) return null;

  const proctoredSet = new Set(proctoredElementIds.map(normalizeId).filter(Boolean));
  if (proctoredSet.has(normalizeId(next.element_id))) return null;

  if (isCertificationLikeUpNext(next)) return null;

  return next;
}

function normalizeProductPromo(productPromo) {
  if (!productPromo || typeof productPromo !== 'object' || Object.keys(productPromo).length === 0) {
    return null;
  }
  const offer = toNumber(productPromo.offer_price);
  const original = toNumber(productPromo.original_price);
  const discount = offer !== null && original !== null && original > offer
    ? Math.round(((original - offer) / original) * 100)
    : null;

  return {
    productId: productPromo.product_id ?? null,
    imageUrl: productPromo.product_icon_url ?? '',
    tagline: productPromo.product_tag_line ?? '',
    offerPrice: offer,
    originalPrice: original !== offer ? original : null,
    discount,
  };
}

function normalizeCertificate({ data, attempt }) {
  const certUrl = attempt?.cert_url;
  const hasCertificateUrl = hasValue(certUrl) && certUrl !== 'None';
  if (!hasCertificateUrl && !data?.cert_id && !data?.cert_name) return null;

  const now = new Date();
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const certName = data?.cert_name ?? data?.element_name ?? attempt?.attempt_name ?? 'STEP Certificate';
  const linkedInOrgId = data?.linkedin_org_id ?? '';
  const certId = data?.cert_id ?? '';
  const linkedInUrl = hasCertificateUrl && linkedInOrgId
    ? `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(certName)}&organizationId=${encodeURIComponent(linkedInOrgId)}&issueYear=${year}&issueMonth=${month}&expirationYear=&expirationMonth=&certUrl=${encodeURIComponent(certUrl)}&certId=${encodeURIComponent(certId)}`
    : '';

  return {
    certId,
    certName,
    certUrl: hasCertificateUrl ? certUrl : '',
    linkedInUrl,
  };
}

export function buildCompletionModel(data, options = {}) {
  const attempt = data?.attempt ?? {};
  const elementType = attempt.element_type ?? '';
  const attemptName = attempt.attempt_name ?? '';
  const showScore = normalizeBooleanFlag(attempt.showScore ?? attempt.show_score, true);
  const isFreeTest = elementType === 'S' && attemptName === 'free' && showScore;
  const isCertificate = elementType === 'S' && !isFreeTest && showScore;
  const isDailyStep = elementType === 'D';

  let mode = 'standard';
  if (isFreeTest) mode = 'free-test';
  else if (isCertificate) mode = 'certificate';
  else if (isDailyStep) mode = 'daily-step';

  return {
    mode,
    elementType,
    attemptName,
    elementName: data?.element_name ?? '',
    attemptId: attempt.attempt_id ?? null,
    upNext: shouldUseCompletionUpNext({
      elementType,
      upNext: data?.upnext ?? data?.upNext,
      proctoredElementIds: options.proctoredElementIds ?? [],
    }),
    productPromo: normalizeProductPromo(data?.product_promo),
    reviewAnswers: data?.review_answers ?? null,
    certificate: normalizeCertificate({ data, attempt }),
    score: data?.answers?.[0]?.score ?? null,
    oLevel: attempt.o_level ?? null,
    oLevelRange: attempt.o_level_range ?? null,
    skillScores: {
      reading: attempt.r_score ?? null,
      writing: attempt.w_score ?? null,
      listening: attempt.l_score ?? null,
      speaking: attempt.s_score ?? null,
    },
    orgId: attempt.org_id ?? null,
    showScore,
    suppressContinue: String(data?.course_id ?? attempt.course_id ?? '') === '507',
    suppressCertificateDownload: String(data?.course_id ?? attempt.course_id ?? '') === '428',
  };
}

export function canShowCompletionScore({ user, model }) {
  if (!model?.showScore) return false;
  if (model.elementType !== 'S') return false;
  const email = String(user?.email ?? user?.login_id ?? user?.loginId ?? '').toLowerCase();
  if (email.includes('naanmudhalvan')) return false;
  return normalizeBooleanFlag(user?.is_retail, true);
}

export function getCompletionScoreDisplay({ user, model }) {
  if (!canShowCompletionScore({ user, model })) return null;

  const score = model.mode === 'free-test'
    ? (hasValue(model.oLevelRange) ? model.oLevelRange : null)
    : (hasValue(model.oLevel) ? model.oLevel : null);

  return score === null ? null : String(score);
}

export function getCompletionContinueState({ model, upNext }) {
  const next = normalizeUpNext(model?.upNext ?? upNext);
  if (!next?.element_id) {
    return {
      next: null,
      showNext: false,
      canContinue: false,
      lockedMessage: '',
      reason: 'missing',
    };
  }

  if (model?.suppressContinue) {
    return {
      next,
      showNext: false,
      canContinue: false,
      lockedMessage: '',
      reason: 'suppressed',
    };
  }

  if (next.lock_status) {
    return {
      next,
      showNext: true,
      canContinue: false,
      lockedMessage: next.lock_message,
      reason: 'locked',
    };
  }

  return {
    next,
    showNext: true,
    canContinue: true,
    lockedMessage: '',
    reason: 'ready',
  };
}
